/** Запросы к бэкенду hub (FastAPI): /api/* — Vite proxy локально, nginx на проде. Сессия — httpOnly cookie. */
import { t } from "./i18n";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const base = `${__BASE_PATH__.replace(/\/$/, "")}/api`;

type Init = NonNullable<Parameters<typeof fetch>[1]> & { json?: unknown };

export async function api<T = unknown>(path: string, init: Init = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`${base}${path}`, {
    credentials: "same-origin",
    ...rest,
    headers: json !== undefined ? { "Content-Type": "application/json", ...headers } : headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = typeof data?.detail === "string" ? data.detail : detail;
    } catch {
      /* тело не JSON */
    }
    // сессия кончилась (тайм-аут неактивности, отзыв доступа) — экран входа покажет AuthProvider
    if (res.status === 401 && !path.startsWith("/auth/")) window.dispatchEvent(new CustomEvent("hub:unauthorized", { detail }));
    throw new ApiError(res.status, detail);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export const apiUrl = (path: string) => `${base}${path}`;

/** Понятный текст ошибки API для тоста */
export function apiErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) return t("Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.");
  const map: Record<string, string> = {
    login_required: t("Сессия закончилась — войдите снова."),
    session_expired: t("Вы долго были неактивны — войдите снова."),
    bad_name: t("Проверьте название: от 1 до 60 символов."),
    bad_slug: t("Адрес: только строчные латинские буквы, цифры и дефис — от 3 до 40 символов."),
    bad_timezone: t("Неизвестный часовой пояс."),
    bad_inactivity: t("Тайм-аут — от 1 до 120 минут."),
    bad_website: t("Сайт должен начинаться с https://"),
    bad_language: t("Такой язык пока не поддерживается."),
    too_long: t("Слишком длинный текст."),
    unknown_user: t("Пользователь не найден или отключён."),
    name_taken: t("Такое название уже есть — выберите другое."),
    teams_only: t("Команды меняют владелец, администраторы и менеджеры."),
    bad_digest: t("Неизвестная частота писем."),
    bad_time: t("Время — в формате ЧЧ:ММ."),
    not_found: t("Не найдено — возможно, уже удалено."),
    bad_phone: t("Телефон в неверном формате — укажите код страны, например +971 50 123 4567."),
    write_first_unsupported: t("В Telegram первым написать нельзя — только ответить тому, кто уже писал."),
    bad_telegram: t("Telegram — это телефон с кодом страны (+971…) или ник вида @name."),
    duplicate_phone: t("Контакт с таким телефоном уже есть."),
    duplicate_email: t("Контакт с такой почтой уже есть."),
    name_or_contact_required: t("Укажите имя, телефон или почту."),
    owner_only_self: t("Агент может назначить ответственным только себя."),
    staff_only: t("Действие доступно владельцу, администраторам и менеджерам."),
    unknown_stage: t("Этап не найден — обновите страницу."),
    unknown_tag: t("Тег не найден — обновите страницу."),
    unknown_team: t("Команда не найдена — обновите страницу."),
    bad_number: t("В числовом поле должно быть число."),
    bad_segment: t("Неизвестная папка."),
    bad_action: t("Неизвестное действие."),
    cannot_delete_default: t("Этап по умолчанию удалить нельзя — сначала назначьте другой этап по умолчанию."),
    default_cannot_be_lost: t("Этап по умолчанию не может быть в группе «потерянные»."),
    system_field: t("Встроенное поле удалить нельзя — его можно только скрыть."),
    key_taken: t("Такой ключ уже занят."),
    bad_key: t("Ключ — латиница, цифры и «_»/«-», начинается с буквы."),
    bad_type: t("Этот тип поля выбрать нельзя: у встроенного поля тип не меняется."),
    bad_color: t("Неизвестный цвет."),
    bad_visibility: t("Неизвестный режим показа поля."),
    file_too_large: t("Файл больше 100 МБ."),
    file_too_large_email: t("По почте Gmail принимает вложения до 25 МБ — отправьте ссылкой или сожмите."),
    mail_send_denied: t("Google не дал отправить письмо из этого ящика: в делегировании домена нужно право gmail.send."),
    too_many_rows: t("В файле больше 200 000 строк."),
    bad_encoding: t("Не удалось прочитать файл: сохраните его в UTF-8 или Windows-1251."),
    empty_file: t("Файл пустой."),
    no_headers: t("В первой строке файла нет заголовков колонок."),
    bad_mapping: t("Сопоставление колонок не прочиталось — начните заново."),
    mapping_needs_contact: t("Сопоставьте хотя бы одну колонку с именем, телефоном или почтой."),
    mapping_twice: t("Одно и то же поле выбрано для двух колонок — оставьте по одной колонке на поле."),
    new_field_admin_only: t("Новые поля карточки может создавать только администратор — выберите для этих колонок «Не импортировать»."),
    exports_only: t("Выгрузка доступна владельцу, администраторам и сотрудникам с правом «Экспорт данных»."),
    dataset_not_ready: t("Эта выгрузка появится вместе с подключением каналов (этап 3)."),
    duplicate_headers: t("В файле есть колонки с одинаковыми заголовками — переименуйте их."),
    export_running: t("Предыдущая выгрузка ещё собирается — дождитесь её."),
    export_expired: t("Ссылка на файл истекла — запросите выгрузку заново."),
    user_disabled: t("Доступ отозван. Обратитесь к администратору."),
    already_exists: t("Пользователь с такой почтой уже есть."),
    bad_email: t("Проверьте почту — адрес выглядит неверно."),
    role_not_allowed: t("Эту роль вы назначить не можете."),
    forbidden: t("Недостаточно прав для этого действия."),
    members_only: t("Управлять участниками могут владелец, администраторы и менеджеры с правом «Управление участниками»."),
    admin_only: t("Раздел доступен владельцу и администраторам."),
    view_as_denied: t("Глазами владельца смотреть нельзя."),
    viewer_read_only: t("У роли «Наблюдатель» только просмотр — изменить это может администратор."),
    cannot_disable_self: t("Нельзя отозвать доступ у самого себя."),
    comment_required: t("Напишите, что осталось не так, — без комментария вернуть в работу нельзя."),
    already_queued: t("Замечание уже в очереди робота."),
    bad_screenshot: t("Скриншот не подошёл: нужен PNG, JPG или WebP до 6 МБ."),
    channels_only: t("Подключать каналы могут владелец, администраторы и сотрудники с правом «Каналы»."),
    gateway_not_configured: t("Шлюз WhatsApp не настроен на сервере (WA_GATEWAY_TOKEN)."),
    mail_not_configured: t("Почта не настроена на сервере: нужен ключ сервисного аккаунта Google (GOOGLE_SERVICE_ACCOUNT_JSON)."),
    delegation_failed: t("Google не пустил в этот ящик: проверьте адрес и что делегирование домена выдано сервисному аккаунту Hub в Admin console."),
    mailbox_exists: t("Этот ящик уже подключён."),
    telegram_not_configured: t("Ключи приложения Telegram ещё не введены — владелец или администратор вводит их один раз в мастере подключения Telegram."),
    bad_telegram_keys: t("Проверьте ключи: api_id — число, api_hash — 32 символа (буквы a–f и цифры)."),
    env_keys: t("Ключи Telegram заданы на сервере — в приложении их менять нельзя."),
    gateway_down: t("Шлюз WhatsApp не отвечает — попробуйте через минуту."),
    gateway_error: t("Шлюз WhatsApp вернул ошибку."),
    not_connected: t("Номер не подключён — переподключите канал в разделе «Каналы»."),
    channel_not_connected: t("Канал WhatsApp не подключён — переподключите его в разделе «Каналы»."),
    not_whatsapp: t("Это архивный канал: сессии WhatsApp у него нет."),
    zoho_not_configured: t("Zoho не подключён: нужны ключи в настройках сервера."),
    channel_gone: t("Канал этого диалога удалён — ответить нельзя."),
    rate_limited: t("Слишком много новых диалогов за час — подождите, чтобы WhatsApp не заблокировал номер."),
    file_missing: t("Файл не найден на сервере."),
    empty_message: t("Сообщение пустое."),
    snippet_missing: t("Шаблон не найден — возможно, его удалили."),
    note_required: t("По правилам при закрытии нужна заметка — напишите, чем закончился диалог."),
    unknown_category: t("Такой категории нет в правилах диалогов."),
    bad_close_after: t("Срок автозакрытия — целое число от 1 до 365."),
    bad_close_unit: t("Единица срока: часы, дни или недели."),
    bad_close_message: t("Текст сообщения при закрытии — от 1 до 1000 знаков."),
    bad_remind: t("Напоминание: 2, 4, 8, 12 или 24 часа."),
    bad_categories: t("Категории: от 1 до 30, названия без повторов."),
    bad_topic: t("Неизвестная тема."),
    too_many_files: t("Не больше 5 файлов."),
    empty_snippet: t("У шаблона нет ни текста, ни файлов."),
    bad_status: t("Неизвестный статус диалога."),
    unknown_channel: t("Канал не найден — обновите страницу."),
    already_registered: t("Этот канал уже привязан к номеру."),
    bad_kind: t("Такой тип канала пока не поддерживается."),
  };
  return map[err.message] || t("Ошибка {status}: {message}", { status: err.status, message: err.message });
}
